import { useLazyQuery } from '@apollo/client/react';
import z from 'zod';

import {
  InfluencerCampaign,
  InfluencerCampaignStatus,
  InfluencerCampaignType,
} from '@anyx/common-graphql';
import { reportAppError } from '@anyx/shared-utils';

import { OnLoadOptionsType, Option } from '../components/condition-builder/types';
import { GET_ALL_INFLUENCER_CAMPAIGNS, GET_SINGLE_AND_ALL_INFLUENCER_CAMPAIGNS } from '../graphql';

interface UseGetInfluencerCampaignOptionsProps {
  storeId: string;
}

const SHARED_QUERY_VALUES = {
  offset: 0,
  limit: 10,
};

const campaignContentParser = z.object({
  id: z.string(),
  campaignType: z.nativeEnum(InfluencerCampaignType),
});

export type GetCampaignOptions = (status: InfluencerCampaignStatus) => OnLoadOptionsType;

type GetAllCampaignOptions = (props: {
  status: InfluencerCampaignStatus;
  keyword?: string;
}) => Promise<Option[]>;

type GetSingleAndAllCampaignOptions = (props: {
  status: InfluencerCampaignStatus;
  campaignValue: string;
  keyword?: string;
}) => Promise<Option[]>;

const buildCampaignOption = (campaigns: InfluencerCampaign[]): Option[] => {
  return campaigns.map(
    (campaign): Option => ({
      label: campaign.name,
      value: JSON.stringify({ id: campaign.id, campaignType: campaign.campaignType }),
      type: campaign.campaignType,
    })
  );
};

const useGetAllCampaignOptions = (storeId: string) => {
  const [fetchAllCampaigns] = useLazyQuery(GET_ALL_INFLUENCER_CAMPAIGNS);

  const getAllCampaignOptions: GetAllCampaignOptions = async ({ status, keyword }) => {
    try {
      const { data } = await fetchAllCampaigns({
        variables: {
          storeId,
          status,
          keyword: keyword ? keyword : undefined,
          ...SHARED_QUERY_VALUES,
        },
      });

      if (!data) throw new Error('No campaigns found');

      return buildCampaignOption([...(data.AllInfluencerCampaigns?.records ?? [])]);
    } catch (error) {
      reportAppError(error);
      return [];
    }
  };

  return { getAllCampaignOptions };
};

export const useGetSingleAndAllCampaignOptions = (storeId: string) => {
  const [fetchSingleAndAllCampaigns] = useLazyQuery(GET_SINGLE_AND_ALL_INFLUENCER_CAMPAIGNS);

  const getSingleAndAllCampaignOptions: GetSingleAndAllCampaignOptions = async ({
    status,
    campaignValue,
    keyword,
  }) => {
    try {
      const campaignContent = campaignContentParser.parse(JSON.parse(campaignValue));

      const { data } = await fetchSingleAndAllCampaigns({
        variables: {
          storeId,
          status,
          keyword: keyword ? keyword : undefined,
          campaignId: campaignContent.id,
          campaignType: campaignContent.campaignType,
          ...SHARED_QUERY_VALUES,
        },
      });

      if (!data) throw new Error('No campaigns found');
      const campaign = data.InfluencerCampaignsByIds?.[0];

      if (!campaign) throw new Error('Missing single Influencer campaign data');

      const options = buildCampaignOption([...(data.AllInfluencerCampaigns?.records ?? [])]);
      const isCampaignInOptions = options.some((option) => option.value === campaignValue);

      if (!isCampaignInOptions) {
        options.push({
          label: campaign.name,
          value: campaignValue,
          type: campaign.campaignType,
        });
      }

      return options;
    } catch (error) {
      reportAppError(error);
      return [];
    }
  };

  return { getSingleAndAllCampaignOptions };
};

export const useGetInfluencerCampaignOptions = ({
  storeId,
}: UseGetInfluencerCampaignOptionsProps) => {
  const { getAllCampaignOptions } = useGetAllCampaignOptions(storeId);
  const { getSingleAndAllCampaignOptions } = useGetSingleAndAllCampaignOptions(storeId);

  const getCampaignOptions: GetCampaignOptions = (status) => (campaignValue) => async (keyword) => {
    if (campaignValue) {
      return getSingleAndAllCampaignOptions({ status, campaignValue, keyword });
    }

    return getAllCampaignOptions({ status, keyword });
  };

  return {
    getCampaignOptions,
  };
};
